'use client';

import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { cn } from '@pickleballinc/ui/lib/utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';

interface NavbarProps {
	isTeamDetailsPage: boolean;
	teamIdOrPlayerId: string;
	className?: string;
}

interface NavItemList {
	label: string;
	href: string;
	isActive: boolean;
}

const pageRoutes = {
	STATS: 'stats',
	SCHEDULE: 'schedule',
	ROSTER: 'roster',
	HISTORY: 'history',
	INFO: 'info',
	RESULTS: 'results',
	MANAGE: 'manage',
	MYTEAMS: 'player-teams',
	MYSCHEDULE: 'schedule',
	MYAVAILABILITY: 'availability'
};

export const extractLastSegment = (path: string) => {
	if (!path) return '';
	const segments = path.split('/').filter((segment) => segment !== '');
	if (segments.length === 0) return '/';
	return segments[segments.length - 1];
};

const generateNavItems = (
	isTeamDetailsPage: boolean,
	teamLeagueSlugOrID: string,
	teamIdOrPlayerId: string,
	currentRoute: string | undefined,
	canSeeManage: boolean,
	pathname: string
): NavItemList[] => {
	const basePath = isTeamDetailsPage
		? `/team-leagues/${teamLeagueSlugOrID}/team-details/${teamIdOrPlayerId}`
		: `/team-leagues/${teamLeagueSlugOrID}/player-details/${teamIdOrPlayerId}`;

	const items: { label: string; route: string }[] = isTeamDetailsPage
		? [
				{ label: 'Stats', route: pageRoutes.STATS },
				{ label: 'Schedule', route: pageRoutes.SCHEDULE },
				{ label: 'Roster', route: pageRoutes.ROSTER },
				{ label: 'History', route: pageRoutes.HISTORY },
				{ label: 'Info', route: pageRoutes.INFO },
				...(canSeeManage ? [{ label: 'Manage', route: pageRoutes.MANAGE }] : [])
			]
		: [
				{ label: 'Stats', route: pageRoutes.STATS },
				{ label: 'Results', route: pageRoutes.RESULTS },
				{ label: 'Teams', route: pageRoutes.MYTEAMS },
				{ label: 'Schedule', route: pageRoutes.MYSCHEDULE },
				{ label: 'Availability', route: pageRoutes.MYAVAILABILITY }
			];

	return items.map(({ label, route }) => ({
		label,
		href: label === 'Manage' ? `${basePath}/${route}/home/active-team` : `${basePath}/${route}`,
		isActive: label === 'Manage' ? pathname.includes(`/${pageRoutes.MANAGE}`) : currentRoute === route
	}));
};

export const TeamDetailsOrPlayerDetailsNavbar = ({ isTeamDetailsPage, teamIdOrPlayerId, className = '' }: NavbarProps) => {
	const pathname = usePathname();
	const store = useOrganizationStore();
	const { gamedayAccess, teamIds } = usePermissionStore();

	const currentRoute = extractLastSegment(pathname);

	const teamLeagueSlugOrID = store.teamLeagueSlug ?? store.teamLeagueUuid;
	const canSeeManage = gamedayAccess || teamIds.includes(teamIdOrPlayerId);

	const generatedNavItems = generateNavItems(isTeamDetailsPage, teamLeagueSlugOrID, teamIdOrPlayerId, currentRoute, canSeeManage, pathname);

	return (
		<nav className={cn(`bg-gray-900 h-10 lg:h-[60px] px-4 sm:px-8 lg:px-0 ${className}`)}>
			<div className="overflow-x-auto no-scrollbar lg:mx-20 h-full">
				<div className="flex mx-auto max-w-screen-xl h-full">
					{generatedNavItems.map((item, index) => (
						<div className="flex" key={item.label}>
							<Link
								key={item.label}
								href={item.href}
								className={cn(
									'flex items-center justify-center h-full px-5 py-2 text-base font-semibold cursor-pointer transition-colors duration-200 whitespace-nowrap text-white',
									{ 'hover:bg-gray-800': !item.isActive },
									{ 'hover:bg-orange-700 bg-[#EF6820]': item.isActive }
								)}
							>
								{formatText({ text: item.label, role: TextRole.TopNavigation })}
							</Link>

							{index < generatedNavItems.length - 1 && !item.isActive && !generatedNavItems.at(index + 1)?.isActive && (
								<div className="flex items-center">
									<div className="w-0.5 bg-white h-6 lg:h-11" />
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</nav>
	);
};
